import { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Cache manager to store audio data in memory during the session
class AudioCacheManager {
  private cache: Map<
    string,
    {
      audioData: string;
      audioFormat: string;
      contentType: string;
      timestamp: number;
    }
  >;

  constructor() {
    this.cache = new Map();
  }

  // Store audio data in the in-memory cache
  storeAudio(messageId: string, audioData: string, audioFormat: string, contentType: string): void {
    this.cache.set(messageId, {
      audioData,
      audioFormat,
      contentType,
      timestamp: Date.now(),
    });
  }

  // Get audio data from the in-memory cache
  getAudio(messageId: string):
    | {
        audioData: string;
        audioFormat: string;
        contentType: string;
        timestamp: number;
      }
    | undefined {
    return this.cache.get(messageId);
  }

  // Check if message audio is in cache
  hasAudio(messageId: string): boolean {
    return this.cache.has(messageId);
  }

  // Clear a specific message's audio
  clearAudio(messageId: string): void {
    this.cache.delete(messageId);
  }

  // Clear all cached audio
  clearAll(): void {
    this.cache.clear();
  }

  // Get count of cached items
  getCacheCount(): number {
    return this.cache.size;
  }

  // Get all message IDs with cached audio
  getCachedMessageIds(): string[] {
    return Array.from(this.cache.keys());
  }
}

// Singleton instance of the cache manager
const audioCacheManager = new AudioCacheManager();

export const useAudioAssist = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSupported, setIsSupported] = useState(false);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
  // Changed back to original name
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [audioProgress, setAudioProgress] = useState<{ [key: string]: number }>({});
  const [cachedAudioCount, setCachedAudioCount] = useState<number>(0);

  const currentAudio = useRef<HTMLAudioElement | null>(null);
  const progressInterval = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Update cached audio count
  useEffect(() => {
    setCachedAudioCount(audioCacheManager.getCacheCount());
  }, []);

  // Check if audio is supported
  useEffect(() => {
    const speechSupported = 'speechSynthesis' in window;
    const audioSupported = typeof Audio !== 'undefined';
    setIsSupported(speechSupported || audioSupported);

    if (!speechSupported && !audioSupported) {
      console.warn('Neither Speech Synthesis nor HTML5 Audio are supported');
    }
  }, []);

  // Stop any current audio and clean up
  const stopSpeech = useCallback(() => {
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;
      currentAudio.current.src = '';
      currentAudio.current = null;
    }

    if ('speechSynthesis' in window && window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    // Clear progress update interval
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current);
      progressInterval.current = null;
    }

    // Abort any in-progress API calls
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setCurrentlyPlayingId(null);
    setIsLoading(null);
  }, []);

  // Update audio progress periodically
  const startProgressTracking = useCallback((messageId: string) => {
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current);
    }

    if (currentAudio.current) {
      progressInterval.current = window.setInterval(() => {
        if (currentAudio.current) {
          const progress = currentAudio.current.currentTime / currentAudio.current.duration;
          setAudioProgress(prev => ({
            ...prev,
            [messageId]: progress,
          }));
        }
      }, 100) as unknown as number;
    }
  }, []);

  // Play audio from a base64 string
  const playAudioFromData = useCallback(
    (messageId: string, audioData: string, contentType: string) => {
      try {
        stopSpeech();

        // Convert base64 to blob
        const binaryString = atob(audioData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: contentType });
        const audioUrl = URL.createObjectURL(blob);

        // Create and play audio element
        const audio = new Audio(audioUrl);
        currentAudio.current = audio;
        setIsLoading(null);
        setCurrentlyPlayingId(messageId);

        // Reset progress
        setAudioProgress(prev => ({
          ...prev,
          [messageId]: 0,
        }));

        // Audio event handlers
        audio.onloadedmetadata = () => {
          startProgressTracking(messageId);
        };

        audio.onended = () => {
          setCurrentlyPlayingId(null);
          setAudioProgress(prev => ({
            ...prev,
            [messageId]: 1, // Set to 100% on completion
          }));
          URL.revokeObjectURL(audioUrl);
          currentAudio.current = null;

          if (progressInterval.current) {
            window.clearInterval(progressInterval.current);
            progressInterval.current = null;
          }
        };

        audio.onerror = error => {
          console.error('Audio playback error:', error);
          setCurrentlyPlayingId(null);
          setIsLoading(null);
          URL.revokeObjectURL(audioUrl);
          currentAudio.current = null;

          if (progressInterval.current) {
            window.clearInterval(progressInterval.current);
            progressInterval.current = null;
          }
        };

        // Play the audio
        audio.play().catch(error => {
          console.error('Failed to play audio:', error);
          setCurrentlyPlayingId(null);
          setIsLoading(null);
        });

        return true;
      } catch (error) {
        console.error('Error playing audio from cache:', error);
        return false;
      }
    },
    [stopSpeech, startProgressTracking]
  );

  // Main function: Generate and play audio for any message
  const playMessageAudio = useCallback(
    async (messageId: string, text: string) => {
      if (!isSupported || !isEnabled || !text.trim()) {
        console.warn('Audio not supported, not enabled, or no text provided');
        return;
      }

      try {
        // Check if we already have this audio in our cache
        if (audioCacheManager.hasAudio(messageId)) {
          const cachedAudio = audioCacheManager.getAudio(messageId);

          if (cachedAudio) {
            const success = playAudioFromData(
              messageId,
              cachedAudio.audioData,
              cachedAudio.contentType
            );

            if (success) {
              return; // Successfully played from cache
            }
            // If playback fails, continue to fetch fresh audio
          }
        }

        // If not cached or playback failed, proceed with generation
        stopSpeech();

        // Set loading state immediately before making the request
        setIsLoading(messageId);

        // Get auth token from URL
        const token = new URLSearchParams(window.location.search).get('token');
        if (!token) {
          throw new Error('Authentication token not found in URL');
        }

        // Request body with message ID for tracking
        const requestBody = {
          text: text,
          language: 'en', // You can make this dynamic based on user settings
          // voice: 'alloy',  // Optional - let backend choose based on language
          token: token,
          message_id: messageId,
        };

        // Create abort controller for this request
        abortControllerRef.current = new AbortController();

        // Make API request to generate audio
        const response = await fetch(`${API_BASE_URL}/chat/audio/generate/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          // Clear loading state in case of error
          setIsLoading(null);
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Audio generation failed: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          // Clear loading state in case of error
          setIsLoading(null);
          throw new Error(result.error || 'Audio generation failed');
        }

        const audioData = result.data;

        // Store in local cache
        audioCacheManager.storeAudio(
          messageId,
          audioData.audio_data,
          audioData.audio_format,
          audioData.content_type
        );

        // Update cache count
        setCachedAudioCount(audioCacheManager.getCacheCount());

        // Play the audio
        playAudioFromData(messageId, audioData.audio_data, audioData.content_type);
      } catch (error) {
        // Check if this is an abort error (user cancelled)
        if (error instanceof DOMException && error.name === 'AbortError') {
          // AbortError is expected when user cancels, ignore
        } else {
          // Clear loading state in case of error
          setIsLoading(null);

          // Fallback to browser speech synthesis

          fallbackToSpeechSynthesis(text, messageId);
        }
      }
    },
    [isSupported, isEnabled, stopSpeech, playAudioFromData]
  );

  // Fallback using browser's built-in speech synthesis
  const fallbackToSpeechSynthesis = useCallback((text: string, messageId: string) => {
    if (!('speechSynthesis' in window)) {
      return;
    }

    try {
      // Clean text for better speech synthesis
      const cleanText = text
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove **bold**
        .replace(/\*(.*?)\*/g, '$1') // Remove *italic*
        .replace(/`(.*?)`/g, '$1') // Remove `code`
        .replace(/#{1,6}\s/g, '') // Remove # headers
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert [links](url) to just text
        .replace(/\n+/g, '. ') // Replace newlines with pauses
        .trim();

      if (!cleanText) {
        console.warn('No clean text available for speech synthesis');
        return;
      }

      const utterance = new SpeechSynthesisUtterance(cleanText);

      // Configure speech settings
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1; // Normal pitch
      utterance.volume = 1; // Full volume

      // Event handlers
      utterance.onstart = () => {
        console.log('Browser speech synthesis started for message:', messageId);
        setCurrentlyPlayingId(messageId);
        setIsLoading(null);

        // Start progress tracking for fallback
        if (progressInterval.current) {
          window.clearInterval(progressInterval.current);
        }

        const startTime = Date.now();
        const estimatedDuration = cleanText.split(' ').length / 2.5; // rough estimate: 2.5 words per second

        progressInterval.current = window.setInterval(() => {
          const elapsed = (Date.now() - startTime) / 1000;
          const progress = Math.min(elapsed / estimatedDuration, 1);
          setAudioProgress(prev => ({
            ...prev,
            [messageId]: progress,
          }));

          if (progress >= 1 && progressInterval.current) {
            window.clearInterval(progressInterval.current);
          }
        }, 100) as unknown as number;
      };

      utterance.onend = () => {
        console.log('Browser speech synthesis completed for message:', messageId);
        setCurrentlyPlayingId(null);
        setAudioProgress(prev => ({
          ...prev,
          [messageId]: 1,
        }));

        if (progressInterval.current) {
          window.clearInterval(progressInterval.current);
          progressInterval.current = null;
        }
      };

      utterance.onerror = event => {
        console.error('Browser speech synthesis error:', event.error);
        setCurrentlyPlayingId(null);
        setIsLoading(null);

        if (progressInterval.current) {
          window.clearInterval(progressInterval.current);
          progressInterval.current = null;
        }
      };

      // Speak using browser's built-in TTS
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Fallback speech synthesis error:', error);
      setCurrentlyPlayingId(null);
      setIsLoading(null);
    }
  }, []);

  // Check if a specific message is currently playing
  const isMessagePlaying = useCallback(
    (messageId: string) => {
      return currentlyPlayingId === messageId;
    },
    [currentlyPlayingId]
  );

  // Check if a specific message is loading - changed back to original name
  const isMessageLoading = useCallback(
    (messageId: string) => {
      return isLoading === messageId;
    },
    [isLoading]
  );

  // Check if a message has cached audio
  const hasMessageCachedAudio = useCallback((messageId: string) => {
    return audioCacheManager.hasAudio(messageId);
  }, []);

  // Get progress for a specific message
  const getMessageProgress = useCallback(
    (messageId: string) => {
      return audioProgress[messageId] || 0;
    },
    [audioProgress]
  );

  // Toggle audio assist on/off
  const toggleAudioAssist = useCallback(() => {
    if (!isSupported) {
      console.warn('Audio is not supported in this browser');
      return;
    }

    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);

    // Stop any current audio when disabling
    if (!newEnabled && (currentlyPlayingId || isLoading)) {
      stopSpeech();
    }

    console.log('Audio Assist:', newEnabled ? 'Enabled' : 'Disabled');
  }, [isEnabled, isSupported, currentlyPlayingId, isLoading, stopSpeech]);

  // Clear all cached audio
  const clearAudioCache = useCallback(() => {
    audioCacheManager.clearAll();
    setCachedAudioCount(0);
    console.log('Audio cache cleared');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, [stopSpeech]);

  return {
    // State
    isEnabled,
    isSupported,
    currentlyPlayingId,
    isLoading, // Changed back to original name
    cachedAudioCount,

    // Actions
    toggleAudioAssist,
    playMessageAudio, // Main function: pass messageId + text, get audio
    stopSpeech,
    clearAudioCache,

    // Utilities
    isMessagePlaying,
    isMessageLoading, // Changed back to original name
    hasMessageCachedAudio,
    getMessageProgress,
  };
};
